import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { Meeting } from '@/types/meetings';
import { MeetingsService } from '@/services/meetingsService';
import { SavedMeetingsService } from '@/services/savedMeetingsService';
import { useAuth } from '@/contexts/AuthContext';
import { getMeetingTimeStatus, formatDisplayTime } from '@/utils/meetingUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateCategory, translatePlatform, normalizeWeekdayValue } from '@/utils/meetingLocale';

export default function MeetingsCarousel() {
  const router = useRouter();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedMeetings, setSavedMeetings] = useState<string[]>([]);

  useEffect(() => {
    loadUpcomingMeetings();
  }, []);

  useEffect(() => {
    const loadSavedMeetings = async () => {
      if (!user) return;

      try {
        const savedMeetingIds = await SavedMeetingsService.getSavedMeetings(user.id);
        setSavedMeetings(savedMeetingIds);
      } catch (error) {
        console.error('Error loading saved meetings in carousel:', error);
      }
    };

    loadSavedMeetings();
  }, [user]);

  const loadUpcomingMeetings = async () => {
    try {
      setLoading(true);
      const allMeetings = await MeetingsService.getAllMeetings();
      const upcomingMeetings = getNextThreeMeetings(allMeetings);
      setMeetings(upcomingMeetings);
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNextThreeMeetings = (allMeetings: Meeting[]): Meeting[] => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
    const currentWeekday = getDayName(now.getDay());

    // Create an array of meetings with their next occurrence time
    const meetingsWithNextTime = allMeetings.map(meeting => {
      const meetingTime = parseTimeToMinutes(meeting.meeting_time);
      const meetingDays = meeting.meeting_weekday.split(';').map(day => day.trim());

      let nextOccurrence = Infinity;

      // Check each day the meeting occurs
      meetingDays.forEach(day => {
        const dayIndex = getDayIndex(day);
        const currentDayIndex = now.getDay();

        let daysUntil = (dayIndex - currentDayIndex + 7) % 7;

        // If it's today and the meeting hasn't happened yet
        if (daysUntil === 0 && meetingTime > currentTime) {
          nextOccurrence = Math.min(nextOccurrence, meetingTime);
        }
        // If it's not today or the meeting has already happened today
        else if (daysUntil > 0 || (daysUntil === 0 && meetingTime <= currentTime)) {
          if (daysUntil === 0) daysUntil = 7; // Next week if meeting already happened today
          const timeUntilMeeting = (daysUntil * 24 * 60) + meetingTime;
          nextOccurrence = Math.min(nextOccurrence, timeUntilMeeting);
        }
      });

      return {
        ...meeting,
        nextOccurrence
      };
    });

    // Sort by live meetings first, then by next occurrence - similar to encontros-online page
    return meetingsWithNextTime
      .filter(meeting => meeting.nextOccurrence !== Infinity)
      .sort((a, b) => {
        // Check if meetings are live (using the same logic as getMeetingTimeStatus)
        const timeStatusA = getMeetingTimeStatus(a.meeting_time, a.meeting_weekday, t);
        const timeStatusB = getMeetingTimeStatus(b.meeting_time, b.meeting_weekday, t);
        
        const isLiveA = timeStatusA.status === 'ongoing';
        const isLiveB = timeStatusB.status === 'ongoing';
        
        // Prioritize live meetings first
        if (isLiveA && !isLiveB) return -1;
        if (!isLiveA && isLiveB) return 1;
        
        // If both are live or both are not live, sort by next occurrence
        return a.nextOccurrence - b.nextOccurrence;
      })
      .slice(0, 3);
  };

  const parseTimeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const getDayName = (dayIndex: number): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayIndex];
  };

  const getDayIndex = (dayName: string): number => {
    // Normalize to English key first (handles both legacy Portuguese and new English keys)
    const normalized = normalizeWeekdayValue(dayName);
    const dayMap: { [key: string]: number } = {
      'sunday': 0,
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6
    };
    return dayMap[normalized] || 0;
  };

  const handleVerTodos = () => {
    router.dismiss();
    router.push('/encontros-online');
  };

  const handleMeetingPress = (meeting: Meeting) => {
    // Navigate to the dedicated meeting page
    router.push(`/meeting/${meeting.id}`);
  };

  // Removed drawer handling - now redirects to encontros-online page

  const TimeStatusChip = ({ meeting }: { meeting: Meeting }) => {
    const timeStatus = getMeetingTimeStatus(meeting.meeting_time, meeting.meeting_weekday, t);
    const isLive = timeStatus.status === 'ongoing';
    
    const getTimeTagStyle = () => {
      return isLive ? [styles.timeTag, styles.timeTagLive] : styles.timeTag;
    };

    const getTimeTextStyle = () => {
      return isLive ? [styles.timeText, styles.timeTextLive] : styles.timeText;
    };
    
    return (
      <View style={getTimeTagStyle()}>
        <Text style={getTimeTextStyle()}>
          {isLive ? t('meetings.status.live') : formatDisplayTime(meeting.meeting_time)}
        </Text>
      </View>
    );
  };

  const MeetingCard = ({ meeting }: { meeting: Meeting }) => (
    <TouchableOpacity 
      style={styles.meetingCard}
      onPress={() => handleMeetingPress(meeting)}
      activeOpacity={0.7}
    >
      <View style={styles.cardHeader}>
        <View style={styles.cardHeaderLeft}>
          <TimeStatusChip meeting={meeting} />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {meeting.plus_required ? (
            <View style={{ backgroundColor: '#F6F7F9', borderRadius: 12, width: 32, height: 32, justifyContent: 'center', alignItems: 'center' }}>
              <Svg width="16" height="16" viewBox="0 0 12 12" fill="none">
                <Path d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z" stroke="#669AB7" strokeLinecap="round" strokeLinejoin="round" />
                <Path d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289" stroke="#669AB7" strokeLinecap="round" strokeLinejoin="round" />
                <Path d="M1.50391 5.10156H10.4954" stroke="#669AB7" strokeLinecap="round" strokeLinejoin="round" />
              </Svg>
            </View>
          ) : null}
          <TouchableOpacity 
            style={styles.externalButton}
            onPress={(e) => {
              e.stopPropagation();
              handleMeetingPress(meeting);
            }}
          >
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M4 12L12 4M12 4H4M12 4V12" stroke="#56554D" strokeWidth="2" />
            </Svg>
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.cardContent}>
        <Text style={styles.meetingTitle}>{meeting.meeting_name}</Text>
        <View style={styles.meetingInfo}>
          <Text style={styles.meetingType}>{translateCategory(meeting.meeting_category, t)}</Text>
          <Text style={styles.meetingPlatform}>
            {translatePlatform(meeting.meeting_place, t)}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.sectionTitle}>{t('meetings.carousel.title')}</Text>
          <TouchableOpacity style={styles.verTodosButton} onPress={handleVerTodos}>
            <Text style={styles.verTodosText}>{t('meetings.carousel.viewAll')}</Text>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M5.83325 3.33325L10.4999 7.99992L5.83325 12.6666" stroke="#7E7D75" strokeWidth="2" strokeLinecap="square" />
            </Svg>
          </TouchableOpacity>
        </View>

        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('meetings.carousel.loading')}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.sectionTitle}>{t('meetings.carousel.title')}</Text>
        <TouchableOpacity style={styles.verTodosButton} onPress={handleVerTodos}>
          <Text style={styles.verTodosText}>{t('meetings.carousel.viewAll')}</Text>
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path d="M5.83325 3.33325L10.4999 7.99992L5.83325 12.6666" stroke="#7E7D75" strokeWidth="2" strokeLinecap="square" />
          </Svg>
        </TouchableOpacity>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContainer}
        style={styles.scrollView}
      >
        {meetings.length > 0 ? (
          meetings.map((meeting) => (
            <MeetingCard key={meeting.id} meeting={meeting} />
          ))
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>{t('meetings.carousel.empty')}</Text>
          </View>
        )}
      </ScrollView>

      {/* Note: Meeting drawer removed - redirects to encontros-online page instead */}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 24,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    flex: 1,
  },
  verTodosButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  verTodosText: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#7E7D75',
  },
  scrollView: {
    marginHorizontal: -24,
  },
  scrollContainer: {
    flexDirection: 'row',
    gap: 8,
    paddingLeft: 24,
    paddingRight: 16,
  },
  meetingCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 24,
    width: 220,
    height: 176,
    justifyContent: 'space-between',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardHeaderLeft: {
    flex: 1,
  },
  timeTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0EEE4',
    borderRadius: 52,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
    height: 32,
  },
  timeTagLive: {
    backgroundColor: '#669AB7', // Blue background for live meetings
  },
  timeText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#56554D',
    lineHeight: 16.8,
  },
  timeTextLive: {
    color: '#ffffff', // White text for live meetings
  },
  externalButton: {
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    padding: 8,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    gap: 4,
  },
  meetingTitle: {
    fontSize: 16,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 22.4,
  },
  meetingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 20,
  },
  meetingType: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#56554D',
    lineHeight: 19.6,
  },
  meetingPlatform: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    lineHeight: 19.6,
  },
  loadingContainer: {
    height: 176,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  loadingText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
  },
  emptyContainer: {
    height: 176,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    width: 220,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    textAlign: 'center',
  },
});